import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Plus } from 'lucide-react';

import { RecordPageShell } from '~/components/layout';
import { Button, Card, CardBody, CardFooter, CardSection, FormActions } from '~/components/ui';
import { HistoryPanel } from '~/components/pages/mls-agreements/HistoryPanel';
import { MembershipList } from '~/components/pages/mls-agreements/MembershipList';
import { isNotFound } from '~/components/pages/mls-agreements/errors';
import {
  fieldId,
  RecordSections,
  SaveError,
  type SectionSpec,
} from '~/components/pages/mls-agreements/fields';
import type { CustomerRecord, Scalar } from '~/components/pages/mls-agreements/types';
import { routes } from '~/app/routes';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { queryErrorMessage } from '~/modules/query';
import {
  useCreateAgreementCustomerMutation,
  useGetAgreementCustomerQuery,
  useUpdateAgreementCustomerMutation,
} from '~/modules/query/mlsAgreements';

type Draft = { name: string; accountId: Scalar; status: string };

const NEW: CustomerRecord = {};

const toDraft = (res: CustomerRecord): Draft => ({
  name: res.name ?? '',
  accountId: res.accountId ?? '',
  status: res.status ?? '',
});

const SECTIONS: SectionSpec<Draft>[] = [
  {
    title: 'General',
    columns: 1,
    fields: [
      { key: 'name', label: 'Name', required: true },
      { key: 'accountId', label: 'BAY account ID', helper: 'Blank for an unlinked customer' },
      { key: 'status', label: 'Status' },
    ],
  },
];

const schema = z.object({ name: z.string().trim().min(1, 'Enter the customer name.') });

const FORM_ID = 'customer-form';
const LIST = `${routes.mlsAgreements}?tab=customers`;

/** Create (`/new`) and edit (`/:id`) one agreement customer (DESIGN.md §1.6). */
export default function CustomerPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const customerId = id ?? '';
  const isNew = !id;

  const query = useGetAgreementCustomerQuery(customerId, { skip: isNew });
  const record = query.currentData as CustomerRecord | undefined;
  const { draft, patch, dirty, reset, markSaved } = useRecordDraft<CustomerRecord, Draft>(
    isNew ? NEW : record,
    { id: id ?? 'new', version: record?.updatedAt, toDraft },
  );
  const guard = useUnsavedGuard(dirty);
  const v = useFormValidation(schema, draft, (field) => fieldId('customer', field));

  const [createCustomer] = useCreateAgreementCustomerMutation();
  const [updateCustomer] = useUpdateAgreementCustomerMutation();
  const createdId = useRef<string | undefined>(undefined);

  const save = useRecordSave<Draft>({
    save: async (d) => {
      if (isNew) createdId.current = (await createCustomer(d).unwrap()).id;
      else
        await updateCustomer({
          id: customerId,
          data: { ...d, updatedAt: record?.updatedAt },
        }).unwrap();
    },
    success: isNew ? 'Customer created' : 'Customer saved',
    failure: 'The customer could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !v.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(createdId.current ? routes.mlsAgreementsCustomer(createdId.current) : LIST, {
      replace: true,
    });
  };

  const title = isNew ? 'Create customer' : record?.name || 'Customer';
  const notFound = !isNew && isNotFound(query.error);
  const failed = query.isError && !notFound;

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[
        { label: 'MLS agreements', href: routes.mlsAgreements },
        { label: 'Customers', href: LIST },
        { label: title },
      ]}
      loading={!draft && !query.isError}
      notFound={notFound}
      notFoundTitle="Customer not found"
      backTo={LIST}
      backLabel="Back to customers"
      error={failed ? { what: 'the customer', message: queryErrorMessage(query.error) } : null}
      onRetry={() => query.refetch()}
      guard={guard}
      testId="customer"
    >
      {draft && (
        <>
          <form id={FORM_ID} noValidate onSubmit={submit}>
            <Card>
              <CardBody className="space-y-6">
                <SaveError message={save.error} />
                <RecordSections
                  prefix="customer"
                  sections={SECTIONS}
                  draft={draft}
                  patch={patch}
                  errors={v.errors}
                  touch={v.touch}
                />
              </CardBody>
              <CardFooter>
                <FormActions
                  form={FORM_ID}
                  {...(isNew
                    ? {
                        discardHref: LIST,
                        discardLabel: 'Cancel',
                        saveLabel: 'Create customer',
                        busyLabel: 'Creating…',
                      }
                    : {
                        onDiscard: () => {
                          reset();
                          v.reset();
                          save.clearError();
                        },
                        disabled: !dirty,
                        disabledReason: 'No changes to save',
                      })}
                  busy={save.busy}
                  saveTestId="customer-save"
                />
              </CardFooter>
            </Card>
          </form>

          {!isNew && (
            <Card>
              <CardBody className="space-y-6">
                <CardSection
                  title="Memberships"
                  actions={
                    <Button
                      size="sm"
                      startIcon={<Plus className="size-4" aria-hidden="true" />}
                      href={`${routes.mlsAgreementsMembershipNew}?customerId=${encodeURIComponent(customerId)}`}
                      testId="customer-new-membership"
                    >
                      Create membership
                    </Button>
                  }
                >
                  <MembershipList
                    memberships={record?.memberships}
                    nameHeader="MLS"
                    nameOf={(m) => m.mlsName ?? `MLS #${m.mlsId}`}
                  />
                </CardSection>

                <CardSection title="History">
                  <HistoryPanel entityType="customer" entityId={customerId} />
                </CardSection>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </RecordPageShell>
  );
}
